import { dynamicParams, customColumnParams } from 'api/helpers'
import { viewFilters } from 'api/transaction-query-params'
import { hiddenFilteredAccountColumns } from './utils'

const columnPosition = (column) =>
  column.column_order !== null && column.column_order !== undefined ? column.column_order : 999

// ThirdLine ID always leads the grid.
const idFirst = (items, isId) => {
  const index = items.findIndex(isId)
  return index > 0 ? [items[index], ...items.filter((_, i) => i !== index)] : items
}

const columnNames = (dashboard, filteredAccountColumns) => {
  const sorted = (dashboard?.columns || []).slice().sort((a, b) => columnPosition(a) - columnPosition(b))
  return [...idFirst(sorted, (c) => c.column_name === 'id').map((c) => c.column_name), ...filteredAccountColumns]
}

// One order across regular, custom and analytics columns, by each one's saved position.
const columnOrder = (dashboard, customColumns, filteredAccountColumns) => {
  const all = [
    ...(dashboard?.columns || []).map((col) => ({ type: 'regular', data: col.column_name, position: columnPosition(col) })),
    ...(customColumns || []).map((col) => ({ type: 'custom', data: col, position: columnPosition(col) })),
    ...(dashboard?.analytics_columns || []).map((col) => ({ type: 'analytics', data: col, position: columnPosition(col) })),
    // Last, so they never displace the columns someone chose.
    ...filteredAccountColumns.map((columnName, index) => ({ type: 'regular', data: columnName, position: 1000 + index }))
  ].sort((a, b) => a.position - b.position)
  return idFirst(all, (c) => c.type === 'regular' && c.data === 'id')
}

// The TransactionTable props that make it a View's grid: its filters, columns and column order.
// Shared by the Data tab and a chart's drill-down so both show the same table.
export const viewGridTableProps = ({ params, dashboard, columns, customColumns, transactionListId = '', showCleared = false }) => {
  // A View can filter on an account segment it doesn't display; add that column so the filter is visible.
  const dashboardColumnNames = (dashboard?.columns || []).map((c) => c.column_name)
  const filteredAccountColumns = hiddenFilteredAccountColumns(params, dashboardColumnNames)

  const queryParams = {
    perPage: params.perPage || 25,
    orderBy: params.orderBy || 'transaction_on',
    orderDirection: params.orderDirection || 'desc',
    transaction_list_id: transactionListId || '',
    ...viewFilters(params),
    showCleared,
    vendors: params.vendors || [],
    excludedVendors: params.excludedVendors || [],
    analytics: (params.analytics || []).map((a) => a?.id).filter(Boolean).join('+'),
    org_ids: params.orgs,
    object_ids: params.objects,
    project_ids: params.projects,
    seg_one_ids: params.segOnes,
    seg_two_ids: params.segTwos,
    seg_three_ids: params.segThrees,
    seg_four_ids: params.segFours,
    seg_five_ids: params.segFives,
    seg_six_ids: params.segSixes,
    seg_seven_ids: params.segSevens,
    seg_eight_ids: params.segEights,
    orgs_operator: params.orgsOperator,
    objects_operator: params.objectsOperator,
    projects_operator: params.projectsOperator,
    seg_ones_operator: params.segOnesOperator,
    seg_twos_operator: params.segTwosOperator,
    seg_threes_operator: params.segThreesOperator,
    seg_fours_operator: params.segFoursOperator,
    seg_fives_operator: params.segFivesOperator,
    seg_sixes_operator: params.segSixesOperator,
    seg_sevens_operator: params.segSevensOperator,
    seg_eights_operator: params.segEightsOperator,
    ...dynamicParams(params, params, columns),
    ...customColumnParams(params, customColumns)
  }

  const tableProps = {
    filtersEnabled: false,
    downloadButton: false,
    sectionId: queryParams.section_id,
    orderBy: queryParams.orderBy,
    orderDirection: queryParams.orderDirection,
    perPage: queryParams.perPage,
    startOn: queryParams.startOn,
    endOn: queryParams.endOn,
    vendors: queryParams.vendors,
    minimumAmount: queryParams.minimumAmount,
    maximumAmount: queryParams.maximumAmount,
    showCleared: queryParams.showCleared,
    excludedVendors: queryParams.excludedVendors,
    analytic: params.analytics,
    orgs: queryParams.org_ids,
    objects: queryParams.object_ids,
    projects: queryParams.project_ids,
    segOnes: queryParams.seg_one_ids,
    segTwos: queryParams.seg_two_ids,
    segThrees: queryParams.seg_three_ids,
    segFours: queryParams.seg_four_ids,
    segFives: queryParams.seg_five_ids,
    segSixes: queryParams.seg_six_ids,
    segSevens: queryParams.seg_seven_ids,
    segEights: queryParams.seg_eight_ids,
    riskScore: queryParams.riskScore,
    orgsOperator: queryParams.orgs_operator,
    objectsOperator: queryParams.objects_operator,
    projectsOperator: queryParams.projects_operator,
    segOnesOperator: queryParams.seg_ones_operator,
    segTwosOperator: queryParams.seg_twos_operator,
    segThreesOperator: queryParams.seg_threes_operator,
    segFoursOperator: queryParams.seg_fours_operator,
    segFivesOperator: queryParams.seg_fives_operator,
    segSixesOperator: queryParams.seg_sixes_operator,
    segSevensOperator: queryParams.seg_sevens_operator,
    segEightsOperator: queryParams.seg_eights_operator,
    riskScoreOperator: queryParams.riskScore,
    columns: columnNames(dashboard, filteredAccountColumns),
    precomputedColumnOrder: columnOrder(dashboard, customColumns, filteredAccountColumns),
    analyticsColumns: dashboard?.analytics_columns || [],
    includeOrdering: true,
    queryParams
  }

  return { queryParams, tableProps }
}
